import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, Menu, Phone, X } from "lucide-react";
import { CONTACT, NAV_LINKS } from "../data/content";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="navbar">
      <div className="hidden border-b border-border/70 bg-white md:block">
        <div className="container-x flex h-9 items-center justify-end gap-6 text-xs text-muted-foreground">
          <a
            href={CONTACT.phoneHref}
            data-testid="topbar-phone"
            className="flex items-center gap-2 transition-colors hover:text-brand"
          >
            <Phone className="h-3.5 w-3.5 text-brand" />
            {CONTACT.phone}
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="topbar-email"
            className="flex items-center gap-2 transition-colors hover:text-brand"
          >
            <Mail className="h-3.5 w-3.5 text-brand" />
            {CONTACT.email}
          </a>
        </div>
      </div>

      <div className="border-b border-border/70 bg-white/95 backdrop-blur-md">
        <div className="container-x relative flex h-[72px] items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img
              src="/skyroute-logo.png"
              alt="SkyRoute Freights Pvt Ltd"
              className="h-14 w-auto object-contain"
            />
          </Link>

          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-[15px] font-medium transition-colors ${
                    isActive ? "text-brand" : "text-foreground/80 hover:text-brand"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" data-testid="nav-cta-quote" className="btn-primary hidden md:inline-flex !px-5 !py-3">
              Get a Quote
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 text-foreground md:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-border bg-white md:hidden"
          >
            <div className="container-x flex flex-col gap-1 py-5">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2.5 text-base font-medium ${
                      isActive ? "bg-[#FEF0DC] text-brand" : "text-foreground/80"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid="mobile-cta-quote" className="btn-primary mt-3 self-start">
                Get a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
